import { HomeIcon, BellIcon, Search, MyLearningMenuIcon, CalendarMenuIcon } from "components";
import {
  Badge,
  BadgeProps,
  Box,
  Button,
  ButtonProps,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import profileAvatar from "assets/icons/icon.profile.png";
import reporting from "assets/icons/icon-reporting.svg";
import incident from "assets/icons/icon-incident.svg";
import contact from "assets/icons/icon-contact.svg";
import manageattendance from "assets/icons/icon-playlist.svg";
import logoutIcon from "assets/icons/icon_log_out_sml.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MouseEvent, useState, CSSProperties } from "react";
import { styled, useTheme } from "@mui/material/styles";
import withStyles from "@mui/styles/withStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useStyles as useHeaderStyles } from "./headerStyle";
import { HEADER_CLICK_TYPES } from "utils/constants";
import MenuIcon from "@mui/icons-material/Menu";
import { MobileDrawer } from "components/mobile-drawer/mobile-drawer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import { useFocusStyles } from "hooks/focusBorder";
import { useTranslation } from "react-i18next";
import portalSettingsManager from "utils/portalSettingsManager";
import HeaderMenuIcons from "../header-menu-icons/header-menu-icons";
import { RootState, useAppDispatch } from "store";
import { useSelector } from "react-redux";
import {
  showAttendanceLink,
  showNotificationLink,
  showReportLink,
  showServiceNowLink,
} from "./helper";

type ActionProps = ButtonProps & { mobile?: boolean };
const Action = withStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "left",
    textTransform: "none",
    textAlign: "left",
    color: "rgba(0, 0, 0, 0.87)",
    paddingLeft: (props: ActionProps) => (props.mobile ? "0px" : "8px"),
    paddingRight: (props: ActionProps) => (props.mobile ? "0px" : "8px"),
    "& div": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    "& svg": {
      marginRight: "10px",
    },
  },
}))(Button);
(Action as any).displayName = "Action";
const useStyle = makeStyles(() => ({
  root: {
    fontSize: "12px",
    lineHeight: "30px",
  },
  icon: {
    fontSize: "1.2rem",
    paddingBottom: ".3rem",
  },
  bgcolor: {
    color: "#000000",
  },
}));

type MobileBadgeProps = BadgeProps & { menu?: boolean };
const MobileBadge = styled(Badge, {
  shouldForwardProp: (prop) => prop !== "menu",
})<MobileBadgeProps>(({ menu }) => ({
  "& .MuiBadge-dot": {
    right: menu ? 3 : -17,
    top: menu ? 3 : -8,
    height: "10px",
    width: "10px",
    border: "2px solid white",
    borderRadius: "50%",
    backgroundColor: "#f50057",
  },
}));
(MobileBadge as any).displayName = "MobileBadge";

const MobileSearch = ({
  showSearch,
  setShowSearch,
  handleNavigate,
}: {
  showSearch: boolean;
  setShowSearch: (val: boolean) => void;
  handleNavigate: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const focusClass = useFocusStyles();
  return (
    <Drawer open={showSearch} anchor="top" slotProps={{ paper: { "aria-label": t("search") } }}>
      <Box
        style={{
          height: "100vh",
          display: "flex",
          padding: "20px",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Search
          onSubmit={() => {
            setShowSearch(false);
            handleNavigate();
          }}
          mobile
        />
        <Button
          variant="outlined"
          onClick={() => setShowSearch(false)}
          style={{
            borderRadius: "0px",
            borderColor: theme.palette.grey["A100"],
            font: "Arial",
            fontWeight: "bold",
            borderWidth: "1px",
            fontSize: "12px",
            paddingTop: "15px",
            paddingBottom: "15px",
            color:
              portalSettingsManager.buttonColors?.normal?.active?.text || theme.palette.grey["800"],
          }}
          className={focusClass.secondaryHover}
        >
          {t("cancel_and_close")}
        </Button>
      </Box>
    </Drawer>
  );
};

type HeaderProps = {
  showSearch: boolean | undefined;
  onClick: (val: string) => void;
  isSurveyCompleted: boolean;
  setOpenNotification: (val: boolean) => void;
  newNotificationCount?: number;
  notificationCount?: number;
  isClearNotification: boolean;
};
export const Header = ({
  showSearch,
  onClick,
  isSurveyCompleted,
  setOpenNotification,
  newNotificationCount,
  notificationCount,
  isClearNotification,
}: HeaderProps): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyle();
  const headerClasses = useHeaderStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mobileProfileDrawerOpen, setMobileProfileDrawerOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const showhamburgermenuicon = useMediaQuery("(max-width: 700px)");
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const focusClass = useFocusStyles();
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const userContext = useSelector((state: RootState) => state.core.userContext?.userContext);

  const showAttendance = showAttendanceLink(userContext);
  const showReport = showReportLink(userContext);
  const showServiceNow = showServiceNowLink(userContext);
  const showNotification = showNotificationLink(
    userContext,
    notificationCount,
    newNotificationCount,
    isSurveyCompleted,
    isClearNotification,
  );

  const handleClose = () => {
    setAnchorEl(null);
  };
  const contactUS = () => {
    setAnchorEl(null);
    onClick(HEADER_CLICK_TYPES.CONTACTUS);
  };
  const handleLogout = () => {
    setAnchorEl(null);
    onClick(HEADER_CLICK_TYPES.LOGOUT);
  };
  const navigateHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
    }
    setMobileProfileDrawerOpen(false);
  };
  const navigateAttendance = () => {
    setAnchorEl(null);
    if (location.pathname !== "/attendance-confirmation") {
      navigate("/attendance-confirmation");
    }
    setMobileProfileDrawerOpen(false);
  };

  const toggleNotifications = (isVisible: boolean) => {
    // setNotificationsOpen(isVisible);
    setOpenNotification(true);
    setMobileProfileDrawerOpen(false);
  };

  const handleNavigateToProfile = () => {
    setAnchorEl(null);
    if (location.pathname !== "/profile") {
      navigate("/profile");
    }
    setMobileProfileDrawerOpen(false);
  };

  const handleNavigateToMyLearning = () => {
    setAnchorEl(null);
    if (location.pathname !== "/catalog/MyLearning") {
      navigate("/catalog/MyLearning");
    }
    setMobileProfileDrawerOpen(false);
  };

  const handleNavigateToMycalendar = () => {
    setAnchorEl(null);
    if (location.pathname !== "/profile/mycalendar") {
      navigate("/profile/mycalendar");
    }
    setMobileProfileDrawerOpen(false);
  };

  const handleNavigateToManageAttendance = () => {
    setAnchorEl(null);
    if (location.pathname !== "/attendance-confirmation") {
      navigate("/attendance-confirmation");
    }
    setMobileProfileDrawerOpen(false);
  };

  const handleNavigate = () => {
    if (location.pathname !== "/catalog") {
      navigate("/catalog");
    }
  };

  const skipToMainContent = () => {
    if (location.pathname !== "/") return;
    const container: HTMLElement | null = document.querySelector(".mainContent");
    if (container) {
      container.tabIndex = -1;
      container.focus();
    }
  };
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={!isMobile && "space-between"}
      height={{ xs: "50px", sm: "70px" }}
      width="100%"
      bgcolor={portalSettingsManager.header?.colors?.base?.back || "#FFFFFF"}
    >
      <MobileSearch
        showSearch={showMobileSearch}
        setShowSearch={setShowMobileSearch}
        handleNavigate={handleNavigate}
      />
      <Box
        display={showhamburgermenuicon ? "flex" : "none"}
        alignItems="center"
        justifyContent="center"
        position={!isMobile ? "absolute" : "unset"}
        left="10px"
      >
        <IconButton
          onClick={() => setMobileProfileDrawerOpen(true)}
          aria-label="Open main menu"
          aria-haspopup="true"
          aria-expanded={mobileProfileDrawerOpen}
        >
          <span className={headerClasses.srOnly}>Open main menu</span>
          {userContext && !userContext.showSurvey && !isSurveyCompleted ? (
            <MobileBadge variant="standard" color="default" menu aria-hidden="true">
              <MenuIcon fontSize="small" style={{ color: "white" }} aria-hidden="true" />
            </MobileBadge>
          ) : (
            <MobileBadge variant="dot" color="secondary" menu aria-hidden="true">
              <MenuIcon fontSize="small" style={{ color: "white" }} aria-hidden="true" />
            </MobileBadge>
          )}
        </IconButton>
      </Box>
      <MobileDrawer
        isOpen={mobileProfileDrawerOpen}
        onVisibilityChange={setMobileProfileDrawerOpen}
      >
        <Box>
          <Box m="15px 0" style={{ fontSize: "14px", fontWeight: "bold" }}>
            {t("menu")}
          </Box>
          <Action onClick={navigateHome} fullWidth mobile={isMobile}>
            <Box position="relative" marginRight="35px">
              <HomeIcon
                style={{
                  position: "absolute",
                  top: "-12px",
                  left: "3px",
                  width: "30px",
                  height: "30px",
                }}
              />
            </Box>
            {t("home")}
          </Action>
          <Action onClick={handleNavigateToMyLearning} fullWidth mobile={isMobile}>
            <div style={{ width: "100%" }}>
              <Box style={{ fontSize: "14px" }}>
                <Box position="relative">
                  <MyLearningMenuIcon
                    style={{
                      color: portalSettingsManager.headerMenuIcon.myLearning.color,
                      backgroundColor:
                        portalSettingsManager.headerMenuIcon.myLearning.backGround || "#000000",
                    }}
                    className={classes.icon}
                  />
                </Box>
                {"My Learning"}
              </Box>
            </div>
          </Action>
          {userContext && (
            <>
              {((notificationCount && notificationCount > 0) ||
                (newNotificationCount && newNotificationCount > 0) ||
                (userContext.showSurvey && !isSurveyCompleted) ||
                !isClearNotification) && (
                <Action onClick={() => toggleNotifications(true)} fullWidth mobile={isMobile}>
                  <Box position="relative" marginRight="35px">
                    <MobileBadge color="secondary" variant="dot">
                      <BellIcon
                        style={{
                          position: "absolute",
                          top: "-12px",
                          width: "35px",
                          height: "35px",
                        }}
                        textColor={portalSettingsManager.header?.colors?.bellIcon?.text || "none"}
                        backgroundColor={classes.bgcolor}
                        borderColor={
                          portalSettingsManager.header?.colors?.bellIcon?.border || "none"
                        }
                      />
                    </MobileBadge>
                  </Box>
                  {`${t("notifications")} (${newNotificationCount})`}
                </Action>
              )}
            </>
          )}
          <Action onClick={handleNavigateToMycalendar} fullWidth mobile={isMobile}>
            <div style={{ width: "100%" }}>
              <Box style={{ fontSize: "14px" }}>
                <Box position="relative">
                  <CalendarMenuIcon
                    style={{
                      color: portalSettingsManager.headerMenuIcon.myLearning.color,
                      backgroundColor:
                        portalSettingsManager.headerMenuIcon.calendar.backGround || "#000000",
                    }}
                    className={classes.icon}
                  />
                </Box>
                {"Calendar"}
              </Box>
            </div>
          </Action>
          <Action onClick={handleNavigateToProfile} fullWidth mobile={isMobile}>
            <div style={{ width: "100%" }}>
              <Box style={{ fontSize: "14px" }}>
                <Box position="relative" marginRight="35px">
                  <img
                    style={{
                      position: "absolute",
                      top: "-10px",
                      left: "5px",
                    }}
                    alt="Profile Avatar"
                    src={profileAvatar}
                  />
                </Box>
                {t("profile")}
              </Box>
            </div>
          </Action>
          <Box
            style={{
              marginTop: "20px",
              marginBottom: "20px",
              maxWidth: "325px",
              borderTop: "1px Solid #d8d8d8",
            }}
          ></Box>
          {showReport && (
            <Action
              onClick={() => {
                setAnchorEl(null);
                onClick(HEADER_CLICK_TYPES.REPORT);
              }}
              fullWidth
              mobile={isMobile}
            >
              <div style={{ width: "100%" }}>
                <Box style={{ fontSize: "14px" }}>
                  <Box position="relative" marginRight="35px">
                    <img
                      style={{
                        position: "absolute",
                        top: "-10px",
                        left: "5px",
                      }}
                      alt="Reporting"
                      src={reporting}
                    />
                  </Box>
                  {t("reporting")}
                </Box>
              </div>
            </Action>
          )}
          {showServiceNow && (
            <Action
              onClick={() => {
                setAnchorEl(null);
                onClick(HEADER_CLICK_TYPES.SERVICENOW);
              }}
              fullWidth
              mobile={isMobile}
            >
              <div style={{ width: "100%" }}>
                <Box style={{ fontSize: "14px" }}>
                  <Box position="relative" marginRight="35px">
                    <img
                      style={{
                        position: "absolute",
                        top: "-10px",
                        left: "5px",
                      }}
                      alt="Incident Management"
                      src={incident}
                    />
                  </Box>
                  {t("incident_management")}
                </Box>
              </div>
            </Action>
          )}
          {showAttendanceLink && (
            <Action onClick={handleNavigateToManageAttendance} fullWidth mobile={isMobile}>
              <Box style={{ fontSize: "14px" }}>
                <Box position="relative" marginRight="35px">
                  <img
                    style={{
                      position: "absolute",
                      left: "5px",
                      width: "20px",
                    }}
                    src={manageattendance}
                    alt="Manage Attendance/Completion"
                  />
                </Box>
                {t("manage_attendance_completion_menu")}
              </Box>
            </Action>
          )}
          <Action onClick={contactUS} fullWidth mobile={isMobile}>
            <Box style={{ fontSize: "14px" }}>
              <Box position="relative" marginRight="35px">
                <img
                  style={{
                    position: "absolute",
                    left: "5px",
                    width: "20px",
                  }}
                  src={contact}
                  alt="Contact Us"
                />
              </Box>
              {t("contact_us")}
            </Box>
          </Action>
          <Action onClick={handleLogout} fullWidth mobile={isMobile}>
            <Box style={{ fontSize: "14px" }}>
              <Box position="relative" marginRight="35px">
                <img
                  style={{
                    position: "absolute",
                    left: "5px",
                    width: "20px",
                  }}
                  src={logoutIcon}
                  alt="Logout icon"
                />
              </Box>
              {t("log_out")}
            </Box>
          </Action>
        </Box>
      </MobileDrawer>
      <Link
        to="/"
        style={
          isMobile
            ? {
                textDecoration: "none",
                display: "flex",
                alignContent: "center",
                flex: "1 1 auto",
              }
            : { textDecoration: "none", flex: "1 1 20%" }
        }
        aria-label={t("aria_label_welcome_header")}
        onClick={skipToMainContent}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent={{ xs: "center", sm: "start" }}
          marginLeft={{ sm: "15px" }}
        >
          {portalSettingsManager &&
            portalSettingsManager?.header &&
            portalSettingsManager?.header?.logo &&
            portalSettingsManager?.header?.logo?.url && (
              <Box
                margin={{ xs: "0 17px 0 0", sm: "0 38px 0 31px" }}
                height="20px"
                display="flex"
                alignItems="center"
              >
                <img
                  src={portalSettingsManager?.header?.logo?.url || ""}
                  style={{
                    width: `${portalSettingsManager?.header?.logo?.widthInPixel || `50`}px`,
                    height: `${portalSettingsManager?.header?.logo?.heightInPixel || `20`}px`,
                  }}
                  alt="Application logo"
                />
              </Box>
            )}
          {portalSettingsManager?.application?.common?.applicationName && (
            <Typography
              style={{
                fontSize: "3.8vh",
                letterSpacing: "3.4px",
                color:
                  portalSettingsManager.header?.colors?.base?.text || theme.palette.grey["800"],
                fontWeight: "bold",
              }}
            >
              {portalSettingsManager.application?.common?.applicationName}
            </Typography>
          )}
        </Box>
      </Link>
      {isMobile ? (
        <Box style={{ display: "unset" }}>
          <IconButton
            onClick={() => setShowMobileSearch(true)}
            className={focusClass.focusItem}
            size="large"
          >
            <SearchIcon style={{ color: "white" }} />
          </IconButton>
        </Box>
      ) : (
        <>
          <Box
            display={{ xs: "none", sm: "flex" }}
            justifyContent="center"
            style={{ height: "40px" }}
            flex="1 1 30%"
          >
            {showSearch && <Search onSubmit={handleNavigate} height="40px" />}
          </Box>
          {portalSettingsManager?.application?.features?.showBrowseCatalogue && (
            <Box display={{ xs: "none", sm: "flex" }} justifyContent="center" flex="1 1 10%">
              <Button
                variant="contained"
                onClick={() => {
                  dispatch({
                    type: "catalogue/shouldRefetchCatalogue",
                    payload: true,
                  });
                  handleNavigate();
                }}
                style={{
                  color: portalSettingsManager.headerMenuIcon.browseCatalog.color,
                  backgroundColor: portalSettingsManager.headerMenuIcon.browseCatalog.backGround,
                  border: "1px solid",
                  whiteSpace: "nowrap",
                  marginLeft: ".4rem",
                }}
              >
                {t(portalSettingsManager.headerMenuIcon.browseCatalog.textKey)}
              </Button>
            </Box>
          )}
        </>
      )}
      <Box
        alignItems="center"
        flex="1 1 40%"
        justifyContent="end"
        height="100%"
        display={{ xs: "none", sm: "flex" }}
      >
        {!showhamburgermenuicon && userContext && (
          <>
            <HeaderMenuIcons
              {...{
                headerMenuSettings: portalSettingsManager.headerMenuIcon,
                notificationProps: {
                  onClick,
                  showNotification,
                  visible: notificationsOpen,
                  toggleNotifications,
                  isMobile,
                  notificationCount: newNotificationCount,
                },
              }}
            />
            <Box>
              <IconButton
                aria-controls="main-menu"
                role="button"
                aria-label={t("three_dot")}
                style={{
                  paddingRight: Boolean(anchorEl) ? "30px" : "",
                  color: portalSettingsManager.header?.colors?.base?.text || "none",
                  border: "2px solid transparent",
                }}
                onClick={handleClick}
                tabIndex={0}
                className={focusClass.focusItem}
                size="large"
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="main-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className={classes.root}
              >
                {showReport && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onClick(HEADER_CLICK_TYPES.REPORT);
                    }}
                    style={{ height: "30px", fontSize: "12px" } as CSSProperties}
                    onMouseEnter={(e) =>
                      ((e.target as HTMLElement).style.backgroundColor = "#f1f1f1")
                    }
                    onMouseLeave={(e) =>
                      ((e.target as HTMLElement).style.backgroundColor = "white")
                    }
                    className={`${classes.root}  ${focusClass.focusItem}`}
                    tabIndex={0}
                  >
                    {t("view_reporting")}
                  </MenuItem>
                )}
                {showServiceNow && (
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      onClick(HEADER_CLICK_TYPES.SERVICENOW);
                    }}
                    style={{ height: "30px", fontSize: "12px" } as CSSProperties}
                    onMouseEnter={(e) =>
                      ((e.target as HTMLElement).style.backgroundColor = "#f1f1f1")
                    }
                    onMouseLeave={(e) =>
                      ((e.target as HTMLElement).style.backgroundColor = "white")
                    }
                    className={`${classes.root}  ${focusClass.focusItem}`}
                    tabIndex={0}
                  >
                    {t("incident_management")}
                  </MenuItem>
                )}
                {showAttendance && (
                  <MenuItem
                    onClick={navigateAttendance}
                    style={{ height: "30px", fontSize: "12px" } as CSSProperties}
                    onMouseEnter={(e) =>
                      ((e.target as HTMLElement).style.backgroundColor = "#f1f1f1")
                    }
                    onMouseLeave={(e) =>
                      ((e.target as HTMLElement).style.backgroundColor = "white")
                    }
                    className={`${classes.root}  ${focusClass.focusItem}`}
                    tabIndex={0}
                  >
                    {t("manage_attendance_completion_menu")}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={contactUS}
                  className={`${classes.root}  ${focusClass.focusItem}`}
                  style={{ height: "30px", fontSize: "12px" } as CSSProperties}
                  onMouseEnter={(e) =>
                    ((e.target as HTMLElement).style.backgroundColor = "#f1f1f1")
                  }
                  onMouseLeave={(e) => ((e.target as HTMLElement).style.backgroundColor = "white")}
                  tabIndex={0}
                >
                  {t("contact_us")}
                </MenuItem>
                <MenuItem
                  onClick={handleLogout}
                  className={`${classes.root}  ${focusClass.focusItem}`}
                  style={{ height: "30px", fontSize: "12px" } as CSSProperties}
                  onMouseEnter={(e) =>
                    ((e.target as HTMLElement).style.backgroundColor = "#f1f1f1")
                  }
                  onMouseLeave={(e) => ((e.target as HTMLElement).style.backgroundColor = "white")}
                  tabIndex={0}
                >
                  {t("log_out")}
                </MenuItem>
              </Menu>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
